/**
 * @fileoverview Module for `src/dashboard/voter-adoption/VoterAdoptionTabs.js`.
 */

import propTypes from 'prop-types';
import VoterAdoptionHeader from './VoterAdoptionHeader.js';
import IndividualAdoptionButtons from './IndividualAdoptionButtons.js';
import PartyAdoptionButtons from './PartyAdoptionButtons.js';
import { segmentTrack } from '../../lib/segment';
import TabUnstyled from '@mui/material/Tab';
import TabsUnstyled from '@mui/material/Tabs';
import { Fragment, useState } from 'react';

VoterAdoptionTabs.propTypes = {
  campaignClosed: propTypes.bool.isRequired,
  campaignState: propTypes.object.isRequired,
  canAdoptSocial: propTypes.bool.isRequired,
  detailBoxClassNames: propTypes.string.isRequired,
  goToTraining: propTypes.func.isRequired,
  handleAdoptionSuccess: propTypes.func.isRequired,
  liveVoterCount: propTypes.number.isRequired,
  maxQual: propTypes.number,
  requestSuperqualStatus: propTypes.func.isRequired,
  setCampaignState: propTypes.func.isRequired,
  updateTraining: propTypes.func.isRequired,
  user: propTypes.object.isRequired,
};

/**
 * Renders adoption mode tabs and routes successful adoption responses into campaign state.
 *
 * @param {Object} params
 * @returns {JSX.Element} Adoption controls and campaign details panel.
 */
export default function VoterAdoptionTabs({
  campaignState,
  maxQual,
  canAdoptSocial,
  user,
  setCampaignState,
  liveVoterCount,
  detailBoxClassNames,
  goToTraining,
  updateTraining,
  campaignClosed,
  handleAdoptionSuccess,
  requestSuperqualStatus,
}) {
  const [adoptionType, setAdoptionType] = useState('individual');
  const { district: campaign, training, superqualNum, qualNum, updatedAvailableVoterCount } = campaignState;

  if (maxQual === null) {
    maxQual = (user.qual_state === 'super_qualified')
      ? parseInt(superqualNum, 10)
      : parseInt(qualNum, 10);
  }
  const maxedOut = (liveVoterCount >= maxQual);

  /**
   * Handles adoption API responses, including paused-campaign fallback behavior.
   *
   * @param {Object} responseData
   * @returns {void}
   */
  const onAdoptionQuerySuccess = (responseData) => {
    if (responseData.campaignStatus === 'PAUSED') {
      setCampaignState({ showPausedModal: true });
    } else {
      onSuccessfulAdoption(responseData);
    }
  };

  /**
   * Tracks successful adoption events and applies resulting campaign state updates.
   *
   * @param {Object} responseData
   * @returns {void}
   */
  const onSuccessfulAdoption = (responseData) => {
    const { voters, districtId, bundleSize, availableVoterCount, id: letterBundleId } = responseData;

    segmentTrack('AdoptVoter:success', {
      campaignSlug: districtId,
      letterBundleId,
      bundleSize,
    });

    const stateUpdate = {};
    if (voters.length === 0) {
      stateUpdate.allAdopted = true;
    }
    if (availableVoterCount !== '') {
      stateUpdate.updatedAvailableVoterCount = availableVoterCount;
    }
    handleAdoptionSuccess();
    setCampaignState(stateUpdate);
  };

  const availableVoters = updatedAvailableVoterCount || campaign.available_voter_count || 0;

  const adoptionTypeConfig = {
    individual: {
      buttons: (
        <IndividualAdoptionButtons
          campaign={ campaign }
          isAdopterMaxedOut={ maxedOut }
          isCampaignClosed={ campaignClosed }
          liveVoterCount={ liveVoterCount }
          onAdoptionQuerySuccess={ onAdoptionQuerySuccess }
        />
      ),
    },
    party: {
      buttons: (
        <PartyAdoptionButtons
          availableVoters={ availableVoters }
          campaign={ campaign }
          isAdopterMaxedOut={ maxedOut }
          isCampaignClosed={ campaignClosed }
          liveVoterCount={ liveVoterCount }
          maxQual={ maxQual }
          onAdoptionQuerySuccess={ onAdoptionQuerySuccess }
        />
      ),
    },
  };

  return (
    <Fragment >
      { canAdoptSocial && (
        <TabsUnstyled
          className='adoption'
          onChange={ (event, newValue) => (setAdoptionType(newValue)) }
          value={ adoptionType }
          variant='fullWidth'
        >
          <TabUnstyled className='adoption individual' label="I'm writing letters myself." value='individual' />
          <TabUnstyled className='adoption party' label="I'm hosting a writing event." value='party' />
        </TabsUnstyled>
      ) }
      <div className={ detailBoxClassNames }>
        <VoterAdoptionHeader
          availableVoters={ availableVoters }
          campaign={ campaign }
          campaignClosed={ campaignClosed }
          goToTraining={ goToTraining }
          maxedOut={ maxedOut }
          requestSuperqualStatus={ requestSuperqualStatus }
          training={ training }
          updateTraining={ updateTraining }
        >
          { adoptionTypeConfig[adoptionType].buttons }
        </VoterAdoptionHeader>
      </div>
    </Fragment >
  );
}
